import type { CSSProperties } from "react";

export const IMAGE_CROPPER_GATE = "bazaar.tapestry.image_cropper";
const IMAGE_CROP_LOAD_TIMEOUT_MS = 5000;

export type AdImageCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export function getCenteredSquareCrop(imageWidth: number, imageHeight: number): AdImageCrop {
  const side = Math.min(imageWidth, imageHeight);
  return {
    x: (imageWidth - side) / (2 * imageWidth),
    y: (imageHeight - side) / (2 * imageHeight),
    width: side / imageWidth,
    height: side / imageHeight,
  };
}

export async function getCenteredSquareCropForImageUrl(
  imageUrl: string,
): Promise<AdImageCrop | undefined> {
  if (typeof Image === "undefined") {
    return undefined;
  }

  return await new Promise((resolve) => {
    const image = new Image();
    const timeout = globalThis.setTimeout(() => finish(undefined), IMAGE_CROP_LOAD_TIMEOUT_MS);
    const finish = (crop: AdImageCrop | undefined) => {
      globalThis.clearTimeout(timeout);
      image.onload = null;
      image.onerror = null;
      resolve(crop);
    };
    image.onload = () => {
      const { naturalHeight, naturalWidth } = image;
      finish(
        naturalWidth > 0 && naturalHeight > 0 ?
          getCenteredSquareCrop(naturalWidth, naturalHeight)
        : undefined,
      );
    };
    image.onerror = () => finish(undefined);
    image.src = imageUrl;
  });
}

export async function getCenteredSquareCropForImageFile(
  file: File,
): Promise<AdImageCrop | undefined> {
  const objectUrl = URL.createObjectURL(file);
  try {
    return await getCenteredSquareCropForImageUrl(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function isValidSquareCrop(
  crop: AdImageCrop | null | undefined,
  imageWidth: number,
  imageHeight: number,
): crop is AdImageCrop {
  if (crop == null || imageWidth <= 0 || imageHeight <= 0) {
    return false;
  }
  const values = [crop.x, crop.y, crop.width, crop.height];
  if (
    values.some((value) => !Number.isFinite(value)) ||
    crop.x < 0 ||
    crop.y < 0 ||
    crop.width <= 0 ||
    crop.height <= 0 ||
    crop.x + crop.width > 1 ||
    crop.y + crop.height > 1
  ) {
    return false;
  }
  const croppedWidth = crop.width * imageWidth;
  const croppedHeight = crop.height * imageHeight;
  return Math.abs(croppedWidth - croppedHeight) <= Math.max(1, croppedWidth, croppedHeight) * 1e-3;
}

export function areImageCropsEqual(
  left: AdImageCrop | null | undefined,
  right: AdImageCrop | null | undefined,
): boolean {
  if (left == null || right == null) {
    return left == null && right == null;
  }
  return (
    left.x === right.x &&
    left.y === right.y &&
    left.width === right.width &&
    left.height === right.height
  );
}

export function getCroppedImageStyle(crop: AdImageCrop): CSSProperties {
  return {
    height: `${100 / crop.height}%`,
    left: `${(-100 * crop.x) / crop.width}%`,
    maxWidth: "none",
    top: `${(-100 * crop.y) / crop.height}%`,
    width: `${100 / crop.width}%`,
  };
}
